import { FIELD_BG, FIELD_DOWNSAMPLE, HEATMAP_COLORS } from './constants';

export type Rgb = [number, number, number];

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export function zMaxOf(z: number[][]): number {
  let max = 0;
  for (const row of z) {
    for (const v of row) {
      const a = Math.abs(v);
      if (a > max) max = a;
    }
  }
  return max || 1;
}

/**
 * Color scale for one session of 0DTE gamma.
 * The 99.2nd percentile through 15:00, matching the comparison chart.
 * The expiry pin is larger than the rest of the day and would wash it out.
 */
export function terrainScale(z: number[][], cutoff: number): number {
  const values: number[] = [];
  const nCols = z[0]?.length ?? 0;
  const last = Math.max(0, Math.min(nCols, cutoff));
  for (const row of z) {
    for (let column = 0; column < last; column++) values.push(Math.abs(row[column] ?? 0));
  }
  if (values.length === 0) return zMaxOf(z);
  values.sort((a, b) => a - b);
  const at = Math.min(values.length - 1, Math.max(0, Math.ceil(values.length * 0.992) - 1));
  return values[at] || 1;
}

/** Index of the cell whose lower edge is <= x, plus the fraction into the next. */
export function locate(grid: number[], x: number): { i: number; f: number } {
  const n = grid.length;
  if (n < 2) return { i: 0, f: 0 };
  const first = grid[0]!;
  const last = grid[n - 1]!;
  if (x <= first) return { i: 0, f: 0 };
  if (x >= last) return { i: n - 2, f: 1 };
  let i = 0;
  while (i < n - 2 && grid[i + 1]! < x) i++;
  const a = grid[i]!;
  const b = grid[i + 1]!;
  return { i, f: (x - a) / (b - a || 1) };
}

/** Same as locate on a 0..n-1 index axis (calendar columns). */
export function locateIndex(n: number, x: number): { i: number; f: number } {
  if (n < 2) return { i: 0, f: 0 };
  if (x <= 0) return { i: 0, f: 0 };
  if (x >= n - 1) return { i: n - 2, f: 1 };
  const i = Math.min(n - 2, Math.floor(x));
  return { i, f: x - i };
}

/** Bilinear sample of z[strike][date] at a price and fractional column. */
export function sampleField(
  strikes: number[],
  z: number[][],
  price: number,
  datePos: number
): number {
  const nDates = z[0]?.length ?? 0;
  if (strikes.length === 0 || nDates === 0) return 0;
  const { i: si, f: sf } = locate(strikes, price);
  const { i: di, f: df } = locateIndex(nDates, datePos);
  const z00 = z[si]?.[di] ?? 0;
  const z01 = z[si]?.[di + 1] ?? z00;
  const z10 = z[si + 1]?.[di] ?? z00;
  const z11 = z[si + 1]?.[di + 1] ?? z10;
  return lerp(lerp(z00, z01, df), lerp(z10, z11, df), sf);
}

export function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [
    parseInt(f.slice(0, 2), 16) || 0,
    parseInt(f.slice(2, 4), 16) || 0,
    parseInt(f.slice(4, 6), 16) || 0,
  ];
}

export function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

const LONG_RGB = hexToRgb(HEATMAP_COLORS.long);
const SHORT_RGB = hexToRgb(HEATMAP_COLORS.short);

/**
 * Light diverging color. No dead band — small |z| still tints.
 * Positive (long γ on the displayed side) → blue; negative → red.
 * Full saturation at |z| = zMax, square-root so the lobes read before the pin.
 */
export function fieldColor(z: number, zMax: number): Rgb {
  const t = clamp(z / (zMax || 1), -1, 1);
  const target = t >= 0 ? LONG_RGB : SHORT_RGB;
  const e = Math.pow(Math.abs(t), 0.5);
  return mix([...FIELD_BG], target, e);
}

/** Dealer-signed grid shown from the customer side. Long gamma stays positive. */
export function customerGamma(field: number[][]): number[][] {
  return field.map((row) => row.map((value) => (value === 0 ? 0 : -value)));
}

export function rgbCss(c: Rgb): string {
  return `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;
}

export function isWeekendIso(iso: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return false;
  const dow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return dow === 0 || dow === 6;
}

/**
 * Rasterize the field, then drawImage into the plot rect.
 * `downsample` above 1 paints a smaller buffer and stretches it.
 * `pixelRatio` sizes that buffer in device pixels so a 1:1 paint stays sharp.
 */
export function paintField(
  ctx: CanvasRenderingContext2D,
  strikes: number[],
  z: number[][],
  zMax: number,
  left: number,
  top: number,
  width: number,
  height: number,
  pixelRatio = 1,
  downsample = FIELD_DOWNSAMPLE
): void {
  const nDates = z[0]?.length ?? 0;
  const nK = strikes.length;
  if (nDates === 0 || nK === 0 || width < 2 || height < 2) return;
  const k0 = strikes[0]!;
  const kN = strikes[nK - 1]!;
  const ds = downsample > 0 ? downsample : FIELD_DOWNSAMPLE;
  const ratio = pixelRatio > 0 ? pixelRatio : 1;
  const dw = Math.max(2, Math.ceil((width * ratio) / ds));
  const dh = Math.max(2, Math.ceil((height * ratio) / ds));

  const off = document.createElement('canvas');
  off.width = dw;
  off.height = dh;
  const octx = off.getContext('2d');
  if (!octx) return;
  const img = octx.createImageData(dw, dh);
  const dateHi = nDates - 1;
  for (let j = 0; j < dh; j++) {
    const price = lerp(kN, k0, j / (dh - 1 || 1));
    for (let i = 0; i < dw; i++) {
      const datePos = lerp(0, dateHi, i / (dw - 1 || 1));
      const c = fieldColor(sampleField(strikes, z, price, datePos), zMax);
      const o = (j * dw + i) * 4;
      img.data[o] = c[0];
      img.data[o + 1] = c[1];
      img.data[o + 2] = c[2];
      img.data[o + 3] = 255;
    }
  }
  octx.putImageData(img, 0, 0);
  ctx.save();
  ctx.imageSmoothingEnabled = ds > 1;
  ctx.drawImage(off, left, top, width, height);
  ctx.restore();
}
